/**
 * @vuelto-surface
 * mobile: ok
 * desktop: ok
 * strategy: unified
 *
 * Carta del mozo sobre la ficha de mesa (backlog 2e).
 * Sumar → Pendiente; no abre el POS. Opciones/variantes = modal secuencial (no anidado).
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  ActivityIndicator,
  FlatList,
  ScrollView,
  StyleSheet,
  Text,
  TextInput,
  TouchableOpacity,
  View,
} from 'react-native';
import type { CategoryRecord, MenuOptionGroup, Product, ProductVariant, SelectedMenuOption } from '../../catalog/domain-api';
import {
  formatOptionsLabel,
  unitPriceWithOptions,
} from '../../catalog/domain-api';
import {
  countSearchProducts,
  fetchMenuOptionsForProducts,
  listCategories,
  listVariantsForProduct,
  peekCachedMenuOptions,
  readPersistedMenuOptions,
} from '../../catalog/infra-api';
import {
  isPosPackageRow,
  isProductCardOutOfStock,
  PosMenuOptionsModal,
  PosVariantPickerModal,
  usePosCatalogPage,
  type PosProductRow,
} from '../../pos/public-api';
import { useHasBusinessCapability } from '../../shell/session/hooks/useEffectiveBusinessCapabilities';
import { ModalShell } from '../../shared/modals/ModalShell';
import { modalExperience } from '../../shared/modals/modalExperience.styles';
import { showInfo } from '../../shared/ui/appToast';
import { presentUnexpectedFailure } from '../../shared/sentry/presentUnexpectedFailure';
import { spacing } from '../../shared/theme/spacing';
import { ticketColors } from '../../shared/theme/ticketColors';
import { typography } from '../../shared/theme/typography';
import { staffDraftUpsertLine, staffGetTableDraft } from '../infra/hospitalityRepo';
import { formatMoney } from '../../shared/format/public-api';

type Props = {
  visible: boolean;
  branchId: string;
  diningTableId: string;
  tableName: string;
  onClose: () => void;
  /** After a successful upsert — parent refreshes inbox. */
  onAdded: () => void | Promise<void>;
};

function newClientLineId(): string {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) {
    return crypto.randomUUID();
  }
  return `staff-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
}

function money(n: number): string {
  return `${formatMoney(Math.round(n))}`;
}

export function TableSumarCartaModal({
  visible,
  branchId,
  diningTableId,
  tableName,
  onClose,
  onAdded,
}: Props) {
  const menuModifiersEnabled = useHasBusinessCapability('menu_modifiers');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<Product['categoryId'] | 'all'>('all');
  const [categories, setCategories] = useState<CategoryRecord[]>([]);
  const [busy, setBusy] = useState(false);
  const [optionsProduct, setOptionsProduct] = useState<Product | null>(null);
  const [optionsGroups, setOptionsGroups] = useState<MenuOptionGroup[]>([]);
  const [optionsLoading, setOptionsLoading] = useState(false);
  const [variantProduct, setVariantProduct] = useState<Product | null>(null);
  const [variants, setVariants] = useState<ProductVariant[]>([]);
  const [pendingLine, setPendingLine] = useState<{
    product: Product;
    unitPrice: number;
    options: SelectedMenuOption[];
    unitNote: string;
    nameOverride?: string;
  } | null>(null);
  const [lineQty, setLineQty] = useState(1);
  const [lineNote, setLineNote] = useState('');
  const [addedCount, setAddedCount] = useState(0);

  const {
    products,
    catalogLoading,
    catalogPageIndex,
    catalogTotalCount,
    catalogPageSize,
    hasMore,
    loadCatalogNextPage,
    loadCatalogPreviousPage,
    reloadCatalog,
  } = usePosCatalogPage({
    currentBranchId: branchId,
    selectedCategory,
    searchQuery,
    itemTypeFilter: 'all',
    posPhysicalStockPositive: false,
    pageSizePreset: 'compact',
    pageSizeDefault: 24,
  });

  const catalogPageCount = Math.max(
    1,
    Math.ceil((catalogTotalCount ?? 0) / Math.max(1, catalogPageSize)),
  );

  useEffect(() => {
    if (!visible) return;
    // Solo al abrir: no depender de reloadCatalog (cambia al tipear y borraba el input).
    setSearchQuery('');
    setSelectedCategory('all');
    setAddedCount(0);
    setPendingLine(null);
    setLineQty(1);
    setLineNote('');
    let alive = true;
    void (async () => {
      try {
        const rows = await listCategories({ includeInactive: false });
        // Solo categorías con ≥1 producto vendible en Sumar (mismo filtro que el listado).
        const withStock = await Promise.all(
          rows.map(async (c) => {
            if (!c.name?.trim()) return null;
            try {
              const n = await countSearchProducts({
                categoryId: c.id,
                branchId,
                includeInactive: false,
                posExcludeMisc: true,
              });
              return n > 0 ? c : null;
            } catch {
              return null;
            }
          }),
        );
        if (alive) {
          setCategories(withStock.filter((c): c is CategoryRecord => c != null));
        }
      } catch {
        if (alive) setCategories([]);
      }
    })();
    return () => {
      alive = false;
    };
  }, [branchId, visible]);

  useEffect(() => {
    if (!visible) return;
    void reloadCatalog();
  }, [visible, reloadCatalog]);

  const categoryChips = useMemo(() => {
    const withProducts = categories.filter((c) => c.name?.trim());
    return [{ id: 'all' as const, name: 'Todos' }, ...withProducts.map((c) => ({ id: c.id, name: c.name }))];
  }, [categories]);

  const openQtySheet = useCallback(
    (args: {
      product: Product;
      unitPrice: number;
      options?: SelectedMenuOption[];
      unitNote?: string | null;
      nameOverride?: string;
    }) => {
      setPendingLine({
        product: args.product,
        unitPrice: args.unitPrice,
        options: args.options ?? [],
        unitNote: args.unitNote ?? '',
        nameOverride: args.nameOverride,
      });
      setLineQty(1);
      setLineNote(args.unitNote ?? '');
    },
    [],
  );

  const upsertLine = useCallback(
    async (args: {
      product: Product;
      qty?: number;
      unitPrice: number;
      unitNote?: string | null;
      options?: SelectedMenuOption[];
      nameOverride?: string;
    }) => {
      setBusy(true);
      try {
        const options = args.options ?? [];
        const addQty = Math.max(1, args.qty ?? 1);
        const optionsLabel = formatOptionsLabel(options) || null;
        const unitNote = args.unitNote?.trim() ? args.unitNote.trim() : null;
        const name = args.nameOverride ?? args.product.name;

        // Merge mismo plato+opciones+nota (como guest): evita 3 filas de Flan x1.
        let clientLineId = newClientLineId();
        let qty = addQty;
        try {
          const current = await staffGetTableDraft(branchId, diningTableId);
          const match = (current.draft?.lines ?? []).find(
            (l) =>
              l.productId === args.product.id &&
              (l.optionsLabel || null) === optionsLabel &&
              (l.unitNote || null) === unitNote &&
              l.name === name &&
              l.addedByRole === 'staff',
          );
          if (match?.clientLineId) {
            clientLineId = match.clientLineId;
            qty = Math.max(1, Number(match.qty) || 0) + addQty;
          }
        } catch {
          // si falla el read, igual insertamos línea nueva
        }

        await staffDraftUpsertLine({
          branchId,
          diningTableId,
          clientLineId,
          productId: args.product.id,
          name,
          qty,
          unitPrice: args.unitPrice,
          unitNote,
          optionsJson: options,
          optionsLabel,
        });
        setAddedCount((n) => n + addQty);
        showInfo(tableName, `${addQty} × ${name} → Pendiente`);
        await onAdded();
      } catch (error) {
        presentUnexpectedFailure({
          module: 'hospitality',
          op: 'tableside.staff_draft_upsert',
          error,
          userMessage: 'No se pudo sumar a la mesa.',
        });
      } finally {
        setBusy(false);
      }
    },
    [branchId, diningTableId, onAdded, tableName],
  );

  const confirmQtySheet = useCallback(() => {
    const pending = pendingLine;
    if (!pending || busy) return;
    setBusy(true);
    const note = lineNote.trim();
    const qty = lineQty;
    setPendingLine(null);
    void upsertLine({
      product: pending.product,
      qty,
      unitPrice: pending.unitPrice,
      options: pending.options,
      unitNote: note || pending.unitNote || null,
      nameOverride: pending.nameOverride,
    });
  }, [busy, lineNote, lineQty, pendingLine, upsertLine]);

  const clearSubModals = useCallback(() => {
    setOptionsProduct(null);
    setOptionsGroups([]);
    setOptionsLoading(false);
    setVariantProduct(null);
    setVariants([]);
    setPendingLine(null);
  }, []);

  const tryAddProduct = useCallback(
    async (product: PosProductRow) => {
      if (busy) return;
      if (product.menuAvailable === false) {
        showInfo('Pausado hoy', 'Reactivá el plato en Catálogo.');
        return;
      }
      if (isProductCardOutOfStock(product)) {
        showInfo('Sin stock', 'Elegí otro o ajustá inventario.');
        return;
      }
      if (product.sellByWeight || product.unitOfMeasure === 'hour') {
        showInfo('Desde el POS', 'Este ítem se suma por peso u hora en caja.');
        return;
      }

      if (menuModifiersEnabled) {
        const cached = peekCachedMenuOptions(product.id);
        if (cached && cached.length > 0) {
          setOptionsProduct(product);
          setOptionsGroups(cached);
          setOptionsLoading(false);
          return;
        }
        if (cached === null) {
          setOptionsLoading(true);
          setOptionsProduct(product);
          setOptionsGroups([]);
          try {
            const map = await fetchMenuOptionsForProducts([product.id]);
            const groups = map.get(product.id);
            if (groups && groups.length > 0) {
              setOptionsGroups(groups);
              setOptionsLoading(false);
              return;
            }
            if (groups && groups.length === 0) {
              setOptionsProduct(null);
              setOptionsLoading(false);
            } else {
              const disk = await readPersistedMenuOptions(product.id);
              if (disk && disk.length > 0) {
                setOptionsGroups(disk);
                setOptionsLoading(false);
                return;
              }
              setOptionsProduct(null);
              setOptionsLoading(false);
              showInfo('Sin conexión', 'No se pudieron cargar las opciones.');
              return;
            }
          } catch {
            const disk = await readPersistedMenuOptions(product.id);
            if (disk && disk.length > 0) {
              setOptionsGroups(disk);
              setOptionsLoading(false);
              return;
            }
            setOptionsProduct(null);
            setOptionsLoading(false);
            showInfo('Sin conexión', 'No se pudieron cargar las opciones.');
            return;
          }
        }
      }

      try {
        const vars = await listVariantsForProduct(product.id);
        if (vars.length > 0) {
          setVariantProduct(product);
          setVariants(vars);
          return;
        }
      } catch {
        // continue without variants
      }

      // Proto: sheet cantidad + “Sumar a la mesa” (no 1-tap silencioso).
      openQtySheet({ product, unitPrice: Number(product.price) || 0 });
    },
    [busy, menuModifiersEnabled, openQtySheet],
  );

  const showCatalog = visible && !optionsProduct && !variantProduct && !pendingLine;

  return (
    <>
      <ModalShell
        visible={showCatalog}
        onClose={onClose}
        onRequestClose={onClose}
        title={`Sumar · ${tableName}`}
        widthVariant="comfortable"
        scrollable={false}
        footer={
          <View style={styles.footerRow}>
            {addedCount > 0 ? (
              <Text style={styles.footerPending} testID="hospitality.mesas.sumar-pending-count">
                Pendiente · {addedCount}
              </Text>
            ) : (
              <View style={{ flex: 1 }} />
            )}
            <TouchableOpacity
              style={addedCount > 0 ? styles.primaryBtn : styles.secondaryBtn}
              onPress={onClose}
              accessibilityRole="button"
              accessibilityLabel="Listo"
              testID="hospitality.mesas.sumar-done"
            >
              <Text style={addedCount > 0 ? styles.primaryLabel : styles.secondaryLabel}>
                Listo
              </Text>
            </TouchableOpacity>
          </View>
        }
      >
        <View style={styles.panel} testID="hospitality.mesas.sumar-carta">
          <Text style={styles.hint}>
            Elegí el plato, cantidad, y Sumá a la mesa. La carta queda abierta para seguir.
          </Text>
          <TextInput
            value={searchQuery}
            onChangeText={setSearchQuery}
            placeholder="Buscar plato o bebida…"
            placeholderTextColor={ticketColors.ink3}
            style={styles.search}
            autoCorrect={false}
            accessibilityLabel="Buscar en la carta"
            testID="hospitality.mesas.sumar-search"
          />
          <View style={styles.catsWrap}>
            <ScrollView
              horizontal
              showsHorizontalScrollIndicator={false}
              keyboardShouldPersistTaps="handled"
              contentContainerStyle={styles.catsContent}
            >
              {categoryChips.map((item) => {
                const on =
                  selectedCategory === item.id ||
                  (item.id === 'all' && selectedCategory === 'all');
                return (
                  <TouchableOpacity
                    key={String(item.id)}
                    style={[styles.catChip, on && styles.catChipOn]}
                    onPress={() => setSelectedCategory(item.id === 'all' ? 'all' : item.id)}
                    accessibilityRole="button"
                    accessibilityState={{ selected: on }}
                  >
                    <Text style={[styles.catLabel, on && styles.catLabelOn]} numberOfLines={1}>
                      {item.name}
                    </Text>
                  </TouchableOpacity>
                );
              })}
            </ScrollView>
          </View>
          {catalogLoading && products.length === 0 ? (
            <View style={styles.loadingBox}>
              <ActivityIndicator color={ticketColors.gold} />
              <Text style={styles.hint}>Cargando carta…</Text>
            </View>
          ) : (
            <FlatList
              data={products.filter((p): p is PosProductRow => !isPosPackageRow(p))}
              keyExtractor={(p) => p.id}
              style={styles.list}
              contentContainerStyle={styles.listContent}
              keyboardShouldPersistTaps="handled"
              ListEmptyComponent={
                <Text style={styles.empty}>Nada con esa búsqueda.</Text>
              }
              renderItem={({ item }) => {
                const sold = isProductCardOutOfStock(item) || item.menuAvailable === false;
                return (
                  <TouchableOpacity
                    style={[styles.dish, sold && styles.dishSold, busy && styles.dishBusy]}
                    disabled={sold || busy}
                    onPress={() => void tryAddProduct(item)}
                    accessibilityRole="button"
                    accessibilityLabel={`Sumar ${item.name}`}
                    testID={`hospitality.mesas.sumar-product-${item.id}`}
                  >
                    <View style={{ flex: 1, minWidth: 0 }}>
                      <Text style={styles.dishName} numberOfLines={2}>
                        {item.name}
                        {sold ? (
                          <Text style={styles.soldTag}>
                            {' '}
                            {item.menuAvailable === false ? 'Pausado' : 'Agotado'}
                          </Text>
                        ) : null}
                      </Text>
                    </View>
                    <Text style={styles.dishPrice}>{money(Number(item.price) || 0)}</Text>
                  </TouchableOpacity>
                );
              }}
            />
          )}
          {catalogPageCount > 1 || hasMore || catalogPageIndex > 0 ? (
            <View style={styles.pager}>
              <TouchableOpacity
                disabled={catalogPageIndex <= 0}
                onPress={() => loadCatalogPreviousPage()}
                accessibilityRole="button"
              >
                <Text style={[styles.pagerBtn, catalogPageIndex <= 0 && styles.pagerDisabled]}>Anterior</Text>
              </TouchableOpacity>
              <Text style={styles.pagerMeta}>
                {catalogPageIndex + 1}
                {(catalogTotalCount ?? 0) > 0 ? ` · ${catalogTotalCount}` : ''}
              </Text>
              <TouchableOpacity
                disabled={!hasMore}
                onPress={() => loadCatalogNextPage()}
                accessibilityRole="button"
              >
                <Text style={[styles.pagerBtn, !hasMore && styles.pagerDisabled]}>Siguiente</Text>
              </TouchableOpacity>
            </View>
          ) : null}
        </View>
      </ModalShell>

      <PosMenuOptionsModal
        visible={Boolean(optionsProduct)}
        product={optionsProduct}
        groups={optionsGroups}
        loading={optionsLoading}
        onCancel={clearSubModals}
        onConfirm={(selected, note) => {
          const product = optionsProduct;
          clearSubModals();
          if (!product) return;
          openQtySheet({
            product,
            unitPrice: unitPriceWithOptions(Number(product.price) || 0, selected),
            options: selected,
            unitNote: note.trim() || null,
          });
        }}
      />

      <PosVariantPickerModal
        visible={Boolean(variantProduct)}
        product={variantProduct}
        variants={variants}
        branchId={branchId}
        onCancel={clearSubModals}
        onSelect={(variant) => {
          const product = variantProduct;
          clearSubModals();
          if (!product) return;
          const suffix = variant.nameSuffix?.trim();
          const name = suffix ? `${product.name} ${suffix}` : product.name;
          const price =
            variant.price != null && Number.isFinite(Number(variant.price))
              ? Number(variant.price)
              : Number(product.price) || 0;
          openQtySheet({
            product,
            unitPrice: price,
            nameOverride: name,
          });
        }}
      />

      <ModalShell
        visible={Boolean(pendingLine)}
        onClose={() => setPendingLine(null)}
        onRequestClose={() => setPendingLine(null)}
        title={pendingLine?.nameOverride ?? pendingLine?.product.name ?? 'Sumar'}
        widthVariant="compact"
        scrollable={false}
        footer={
          <View style={styles.footerRow}>
            <TouchableOpacity
              style={styles.secondaryBtn}
              onPress={() => setPendingLine(null)}
              accessibilityRole="button"
              accessibilityLabel="Cancelar"
            >
              <Text style={styles.secondaryLabel}>Cancelar</Text>
            </TouchableOpacity>
            <TouchableOpacity
              style={[styles.primaryBtn, busy && styles.dishBusy]}
              disabled={busy}
              onPress={confirmQtySheet}
              accessibilityRole="button"
              accessibilityLabel="Sumar a la mesa"
              testID="hospitality.mesas.sumar-confirm-line"
            >
              <Text style={styles.primaryLabel}>{busy ? '…' : 'Sumar a la mesa'}</Text>
            </TouchableOpacity>
          </View>
        }
      >
        {pendingLine ? (
          <View style={styles.qtyPanel} testID="hospitality.mesas.sumar-qty-sheet">
            <Text style={styles.qtyPrice}>{money(pendingLine.unitPrice * lineQty)}</Text>
            {pendingLine.options.length > 0 ? (
              <Text style={styles.qtyMeta}>{formatOptionsLabel(pendingLine.options)}</Text>
            ) : null}
            <View style={styles.qtyRow}>
              <Text style={styles.qtyLabel}>Cantidad</Text>
              <View style={styles.qtyStepper}>
                <TouchableOpacity
                  style={styles.qtyBtn}
                  onPress={() => setLineQty((q) => Math.max(1, q - 1))}
                  accessibilityRole="button"
                  accessibilityLabel="Menos"
                  testID="hospitality.mesas.sumar-qty-minus"
                >
                  <Text style={styles.qtyBtnLabel}>−</Text>
                </TouchableOpacity>
                <Text style={styles.qtyVal} testID="hospitality.mesas.sumar-qty-value">
                  {lineQty}
                </Text>
                <TouchableOpacity
                  style={styles.qtyBtn}
                  onPress={() => setLineQty((q) => q + 1)}
                  accessibilityRole="button"
                  accessibilityLabel="Más"
                  testID="hospitality.mesas.sumar-qty-plus"
                >
                  <Text style={styles.qtyBtnLabel}>+</Text>
                </TouchableOpacity>
              </View>
            </View>
            <Text style={styles.qtyLabel}>Nota para cocina</Text>
            <TextInput
              value={lineNote}
              onChangeText={setLineNote}
              placeholder="Ej. sin sal, punto medio…"
              placeholderTextColor={ticketColors.ink3}
              style={styles.noteInput}
              multiline
              accessibilityLabel="Nota para cocina"
            />
          </View>
        ) : null}
      </ModalShell>
    </>
  );
}

const styles = StyleSheet.create({
  panel: {
    gap: spacing.sm,
    minHeight: 320,
  },
  hint: {
    ...typography.caption,
    color: ticketColors.ink3,
  },
  search: {
    ...modalExperience.formInput,
    marginTop: 0,
  },
  catsWrap: {
    maxHeight: 44,
  },
  catsContent: {
    gap: 6,
    paddingVertical: 2,
    paddingRight: 8,
  },
  catChip: {
    borderRadius: 999,
    borderWidth: 1,
    borderColor: ticketColors.ruleSoft,
    backgroundColor: ticketColors.paper,
    paddingHorizontal: 12,
    paddingVertical: 7,
  },
  catChipOn: {
    backgroundColor: ticketColors.ink,
    borderColor: ticketColors.ink,
  },
  catLabel: {
    fontSize: 12,
    fontWeight: '700',
    color: ticketColors.ink2,
  },
  catLabelOn: {
    color: '#fff',
  },
  list: {
    maxHeight: 360,
  },
  listContent: {
    gap: 6,
    paddingBottom: spacing.sm,
  },
  dish: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.sm,
    backgroundColor: ticketColors.paper,
    borderWidth: 1,
    borderColor: ticketColors.ruleSoft,
    borderRadius: 12,
    paddingHorizontal: 12,
    paddingVertical: 11,
  },
  dishSold: {
    opacity: 0.45,
  },
  dishBusy: {
    opacity: 0.7,
  },
  dishName: {
    fontSize: 15,
    fontWeight: '700',
    color: ticketColors.ink,
  },
  soldTag: {
    fontSize: 11,
    fontWeight: '800',
    color: '#9F1239',
    textTransform: 'uppercase',
  },
  dishPrice: {
    fontSize: 14,
    fontWeight: '800',
    color: ticketColors.ink,
    fontVariant: ['tabular-nums'],
  },
  loadingBox: {
    paddingVertical: 40,
    alignItems: 'center',
    gap: spacing.sm,
  },
  empty: {
    ...typography.caption,
    color: ticketColors.ink3,
    textAlign: 'center',
    paddingVertical: 24,
  },
  pager: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingTop: spacing.xs,
  },
  pagerBtn: {
    fontSize: 13,
    fontWeight: '700',
    color: ticketColors.gold,
  },
  pagerDisabled: {
    opacity: 0.35,
  },
  pagerMeta: {
    fontSize: 12,
    fontWeight: '600',
    color: ticketColors.ink3,
  },
  footerRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'flex-end',
    gap: spacing.sm,
  },
  footerPending: {
    flex: 1,
    fontSize: 13,
    fontWeight: '700',
    color: ticketColors.ink2,
  },
  secondaryBtn: {
    borderRadius: 10,
    borderWidth: 1,
    borderColor: ticketColors.ruleSoft,
    backgroundColor: ticketColors.paper2,
    paddingHorizontal: 16,
    paddingVertical: 12,
  },
  secondaryLabel: {
    fontSize: 14,
    fontWeight: '700',
    color: ticketColors.ink,
  },
  primaryBtn: {
    borderRadius: 10,
    backgroundColor: ticketColors.gold,
    paddingHorizontal: 16,
    paddingVertical: 12,
  },
  primaryLabel: {
    fontSize: 14,
    fontWeight: '800',
    color: '#fff',
  },
  qtyPanel: {
    gap: spacing.sm,
  },
  qtyPrice: {
    fontSize: 22,
    fontWeight: '800',
    color: ticketColors.ink,
    fontVariant: ['tabular-nums'],
  },
  qtyMeta: {
    fontSize: 13,
    fontWeight: '600',
    color: ticketColors.ink2,
  },
  qtyRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginTop: spacing.sm,
  },
  qtyLabel: {
    fontSize: 13,
    fontWeight: '700',
    color: ticketColors.ink2,
  },
  qtyStepper: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
    borderWidth: 1,
    borderColor: ticketColors.ruleSoft,
    borderRadius: 999,
    padding: 4,
  },
  qtyBtn: {
    width: 34,
    height: 34,
    borderRadius: 17,
    backgroundColor: ticketColors.paper2,
    alignItems: 'center',
    justifyContent: 'center',
  },
  qtyBtnLabel: {
    fontSize: 18,
    fontWeight: '800',
    color: ticketColors.ink,
  },
  qtyVal: {
    minWidth: 24,
    textAlign: 'center',
    fontSize: 16,
    fontWeight: '800',
    color: ticketColors.ink,
  },
  noteInput: {
    ...modalExperience.formInput,
    minHeight: 64,
    textAlignVertical: 'top',
  },
});
